import clsx from 'clsx';
import { isValidElement, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ColumnPicker, type ColumnOption, type SortState } from './ColumnPicker';
import { Pagination } from './Pagination';
import { TABLE_BAR_DENSITY, TD_DENSITY, TH_DENSITY } from './tableDensity';

/**
 * Recursively check if a rendered cell is just an em-dash / en-dash / hyphen
 * placeholder — so mobile cards can skip label:value rows that would only
 * show "LABEL —". Walks React element children so `<span>—</span>` counts
 * as empty just like the plain string "—".
 */
function isDashLike(node: ReactNode): boolean {
  if (node == null || node === false || node === true) return true;
  if (typeof node === 'string') {
    const t = node.trim();
    return t === '' || t === '—' || t === '–' || t === '-';
  }
  if (typeof node === 'number') return false;
  if (Array.isArray(node)) return node.every(isDashLike);
  if (isValidElement(node)) {
    const kids = (node.props as { children?: ReactNode }).children;
    return kids !== undefined && isDashLike(kids);
  }
  return false;
}

/**
 * Flatten a rendered cell down to its plain text so a single search box can
 * match against every column — including formatted/derived values (dates,
 * badges, joined name+code blocks). Walks React children just like isDashLike.
 */
function cellText(node: ReactNode): string {
  if (node == null || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(cellText).join(' ');
  if (isValidElement(node)) {
    const kids = (node.props as { children?: ReactNode }).children;
    return kids !== undefined ? cellText(kids) : '';
  }
  return '';
}

/** What a masked column shows in place of its values. */
const MASKED = <span className="select-none tracking-widest text-[var(--text-muted)]" title="Value hidden — untick Hide values in the column picker to show it">xxxxx</span>;

interface Column<T> {
  key: string;
  header: ReactNode;
  /** Fixed width. Enforced only when the enclosing table is tableLayout="fixed". */
  width?: string;
  /** Min width. Column can grow past this to fit content — works in auto layout. */
  minWidth?: string;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'center' | 'right';
  /** Human-readable label for the column picker. Falls back to the header
   *  when it's a plain string, then to the key. */
  pickerLabel?: string;
  /** Hide this column from the picker (always visible, never listed). Use
   *  for action columns that must always show. */
  alwaysVisible?: boolean;
}

interface Props<T> {
  title?: ReactNode;
  count?: string;
  /** Optional control rendered at the right of the title bar (e.g. an Export button). */
  titleAction?: ReactNode;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  loading?: boolean;
  empty?: ReactNode;
  footer?: ReactNode;
  /** 'fixed' enforces explicit column widths; 'auto' (default) lets browser size to content. */
  tableLayout?: 'auto' | 'fixed';
  /**
   * On mobile (< sm), render each row as a card instead of a horizontally-
   * scrolling table row. The first column becomes the card header; the rest
   * render as label:value pairs. Default true — pass false to keep the
   * desktop table on mobile (rare; use only when the horizontal scroll is
   * genuinely better than stacked cards for that data).
   */
  mobileCards?: boolean;
  /** When set, a column-visibility picker is shown in the title bar and the
   *  user's choices persist to localStorage under `table.<storageKey>.hiddenCols`. */
  storageKey?: string;
  /** Free-text query. When non-empty, rows are filtered client-side to those
   *  whose visible columns' text contains it (case-insensitive) — so one search
   *  box matches every column of the table. Whitespace splits into AND terms. */
  search?: string;
  /** Opt-in client-side pagination: slices the (search-filtered) rows and shows
   *  a Pagination bar in the footer. */
  paginated?: boolean;
  /** Rows per page when `paginated` (default 25). */
  defaultPageSize?: number;
  /** Render the column-visibility picker at the far right (after `titleAction`)
   *  instead of its default spot (right after the count). Opt-in per table. */
  pickerLast?: boolean;
  /** Sorting offered in the column picker (the page does the sorting, e.g. on the server). */
  sortOptions?: { key: string; label: string }[];
  sort?: SortState | null;
  onSortChange?: (next: SortState | null) => void;
}

export function Table<T>({
  title,
  count,
  titleAction,
  columns,
  rows,
  rowKey,
  loading,
  empty,
  footer,
  tableLayout = 'auto',
  mobileCards = true,
  storageKey,
  search,
  paginated = false,
  defaultPageSize = 25,
  pickerLast = false,
  sortOptions,
  sort,
  onSortChange,
}: Props<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const titleBarRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState('calc(100vh - 280px)');

  // Column visibility (only active when storageKey is provided).
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    if (!storageKey) return new Set();
    try {
      const raw = localStorage.getItem(`table.${storageKey}.hiddenCols`);
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  });
  useEffect(() => {
    if (!storageKey) return;
    localStorage.setItem(`table.${storageKey}.hiddenCols`, JSON.stringify(Array.from(hiddenCols)));
  }, [storageKey, hiddenCols]);

  // Columns whose values are masked as "xxxxx" — the column stays, its values don't show
  // (screen, phone cards and search alike). Remembered per table like hiddenCols.
  const [maskedCols, setMaskedCols] = useState<Set<string>>(() => {
    if (!storageKey) return new Set();
    try {
      const raw = localStorage.getItem(`table.${storageKey}.maskedCols`);
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  });
  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(`table.${storageKey}.maskedCols`, JSON.stringify(Array.from(maskedCols))); } catch { /* storage blocked */ }
  }, [storageKey, maskedCols]);

  const pickerOptions: ColumnOption[] = useMemo(() => {
    if (!storageKey) return [];
    return columns
      .filter((c) => !c.alwaysVisible)
      .map((c, i) => {
        const headerStr = typeof c.header === 'string' ? c.header : '';
        const label = c.pickerLabel || headerStr || c.key || `Column ${i + 1}`;
        const isFirst = c === columns[0];
        const emptyHeader = !headerStr && !c.pickerLabel;
        return {
          key: c.key,
          label,
          // First column (identifier) + action columns (empty header) stay locked.
          locked: isFirst || emptyHeader,
          // Any data column can be masked; action-button columns can't.
          maskable: !emptyHeader,
        };
      });
  }, [columns, storageKey]);

  const visibleColumns = useMemo(() => {
    const shown = storageKey ? columns.filter((c) => c.alwaysVisible || !hiddenCols.has(c.key)) : columns;
    if (!storageKey || maskedCols.size === 0) return shown;
    return shown.map((c) => (maskedCols.has(c.key) ? { ...c, cell: () => MASKED } : c));
  }, [columns, hiddenCols, maskedCols, storageKey]);

  // Universal client-side search: keep rows whose visible columns' combined
  // text contains every whitespace-separated term. Runs only when a query is
  // present, so unsearched tables pay nothing.
  const displayRows = useMemo(() => {
    const terms = (search ?? '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return rows;
    return rows.filter((row) => {
      const hay = visibleColumns.map((c) => cellText(c.cell(row))).join(' ').toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [rows, visibleColumns, search]);

  // Opt-in client-side pagination over the (search-filtered) rows.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  useEffect(() => { setPage(1); }, [search]);
  const totalRows = displayRows.length;
  const pageSafe = paginated ? Math.min(page, Math.max(1, Math.ceil(totalRows / pageSize))) : 1;
  const pagedRows = paginated ? displayRows.slice((pageSafe - 1) * pageSize, pageSafe * pageSize) : displayRows;

  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      // Use document-relative top (rect.top + scrollY) — otherwise a scrolled
      // page shrinks the measured top, we oversize the table, body grows,
      // more scroll appears, positive-feedback loop. docTop is stable.
      const rect = containerRef.current.getBoundingClientRect();
      const docTop = rect.top + window.scrollY;
      const titleH = titleBarRef.current?.getBoundingClientRect().height ?? 0;
      const footerH = footerRef.current?.getBoundingClientRect().height ?? 0;
      // With body:overflow:hidden the browser clips instead of scrolling, so
      // the pagination can sit almost flush with the viewport bottom. A few
      // px keeps it off the exact edge.
      const bottomGap = 4;
      const avail = window.innerHeight - docTop - titleH - footerH - bottomGap;
      setMaxHeight(`${Math.max(120, avail)}px`);
    };
    update();
    const r = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(r);
      window.removeEventListener('resize', update);
    };
  }, [footer, title, count]);

  return (
    <div ref={containerRef} className="overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl">
      {(title || count || storageKey || titleAction) && (
        <div ref={titleBarRef} className={clsx('flex items-center justify-between gap-2 border-b border-[var(--border-color)]', TABLE_BAR_DENSITY)}>
          {title ? <div className="text-sm font-extrabold text-[var(--text-primary)]">{title}</div> : <span />}
          <div className="flex items-center gap-3">
            {count && <div className="text-xs text-[var(--text-muted)]">{count}</div>}
            {!pickerLast && storageKey && pickerOptions.length > 0 && (
              <ColumnPicker hidden={hiddenCols} onChange={setHiddenCols} options={pickerOptions} masked={maskedCols} onMaskChange={setMaskedCols} sortOptions={sortOptions} sort={sort} onSortChange={onSortChange} />
            )}
            {titleAction}
            {pickerLast && storageKey && pickerOptions.length > 0 && (
              <ColumnPicker hidden={hiddenCols} onChange={setHiddenCols} options={pickerOptions} masked={maskedCols} onMaskChange={setMaskedCols} sortOptions={sortOptions} sort={sort} onSortChange={onSortChange} />
            )}
          </div>
        </div>
      )}
      {/* Mobile card view — denser layout: title + tail (usually actions)
          share the header row; middle columns render in a 2-column grid so
          the card uses the full screen width. Default on <sm. */}
      {mobileCards && (
        <div
          className="overflow-y-auto sm:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          // Use `height` (not `maxHeight`) so the card visually fills the
          // available viewport even when the list is short — no dead
          // whitespace below a card with 1 or 2 rows.
          style={{ height: maxHeight, WebkitOverflowScrolling: 'touch' }}
        >
          {loading && (
            <div className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">
              Loading…
            </div>
          )}
          {!loading && displayRows.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
              {empty ?? 'No records found'}
            </div>
          )}
          {!loading &&
            pagedRows.map((row) => {
              // Split columns for a 4-zone mobile card:
              //   header title  — first column (identifier)
              //   header status — any column whose header contains "status"
              //                   (right-aligned, opposite the title)
              //   body          — remaining info columns (2-col grid)
              //   footer        — action-like columns (empty header, or
              //                   headers like "QR / Barcode", "Labels",
              //                   "Actions", "Print") — grouped together
              //                   into an actions strip at the bottom
              const ACTION_HEADER_RE = /^\s*(actions?|qr(\s*\/\s*barcode)?|barcode|labels?|print|download)\s*$/i;
              const first = visibleColumns[0];
              const statusCol = visibleColumns.find(
                (c, i) => i !== 0 && /status/i.test(String(c.header ?? '')),
              ) ?? null;
              const footerCols = visibleColumns.filter((c, i) => {
                if (i === 0) return false;
                if (c === statusCol) return false;
                if (!c.header) return true;
                return ACTION_HEADER_RE.test(String(c.header));
              });
              const middle = visibleColumns.filter((c, i) => {
                if (i === 0) return false;
                if (c === statusCol) return false;
                return !footerCols.includes(c);
              });
              return (
                <div
                  key={rowKey(row)}
                  className="border-b border-l-4 border-l-accent border-[var(--border-color)] px-2.5 py-2 last:border-b-0 odd:bg-[var(--bg-secondary)]/40"
                >
                  {/* Header — title left, status right */}
                  <div className="mb-1.5 flex items-start justify-between gap-2">
                    {first && (
                      <div className="min-w-0 flex-1 text-[13px] font-extrabold text-[var(--text-primary)]">
                        {first.cell(row)}
                      </div>
                    )}
                    {statusCol && (
                      <div className="flex flex-shrink-0 flex-col items-end gap-1">
                        {statusCol.cell(row)}
                      </div>
                    )}
                  </div>
                  {/* Body — inline "LABEL: value" rows, uniform label column
                      so every value column aligns. Rows whose value renders
                      as just "—" (or an empty string) are skipped on mobile
                      to trim clutter — the column-picker still shows them
                      on desktop. */}
                  {middle.length > 0 && (() => {
                    const visible = middle.filter((c) => !isDashLike(c.cell(row)));
                    if (visible.length === 0) return null;
                    return (
                      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
                        {visible.map((c) => (
                          <div key={c.key} className="col-span-2 grid grid-cols-subgrid items-baseline">
                            <dt className="truncate text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                              {c.header}
                            </dt>
                            <dd className="min-w-0 break-words font-semibold text-[var(--text-primary)]">
                              {c.cell(row)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    );
                  })()}
                  {/* Footer — all action-like columns share one strip.
                      All buttons stay on a single line: no wrap, no scroll.
                      Buttons inside get a compacted mobile size via CSS
                      selector so a typical [icon+label] × 4 row fits at
                      375px without truncation. */}
                  {footerCols.length > 0 && (
                    <div
                      className={
                        'mt-2 flex gap-1 border-t border-[var(--border-color)]/50 pt-2 ' +
                        // Column wrappers + any per-cell flex containers use
                        // display:contents so every underlying button becomes a
                        // direct sibling of the strip. That way N buttons share
                        // the row evenly (via !flex-1) regardless of how the
                        // page groups them into columns.
                        '[&>div]:!contents [&>div>div]:!contents ' +
                        '[&_button]:!px-2 [&_button]:!py-1 [&_button]:!text-[11px] [&_button]:!gap-1 [&_button]:!flex-1 [&_button]:!min-w-0 [&_button]:!justify-center'
                      }
                    >
                      {footerCols.map((c) => (
                        <div key={c.key}>{c.cell(row)}</div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Desktop table (or mobile too if mobileCards={false}).
          Horizontal scrollbar stays VISIBLE when columns overflow the
          container width — critical for wide tables (MRN/PO/GRN have 12+
          columns) so users know they can scroll sideways. Styled thin so
          it doesn't hog vertical real estate. Vertical scroll uses the
          calculated maxHeight and stays chromeless via the same thin
          treatment — an SB shows only if row count exceeds available height. */}
      <div
        className={clsx(
          'overflow-x-auto overflow-y-auto',
          // Thin, subtle scrollbars — WebKit
          '[&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5',
          '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--border-color)]',
          '[&::-webkit-scrollbar-thumb:hover]:bg-[var(--text-muted)]',
          '[&::-webkit-scrollbar-track]:bg-transparent',
          mobileCards && 'hidden sm:block',
        )}
        style={{
          maxHeight,
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--border-color) transparent',
          scrollbarGutter: 'stable',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <table className="w-full border-collapse" style={{ tableLayout }}>
          <thead className="sticky top-0 z-10">
            <tr>
              {visibleColumns.map((c) => (
                <th
                  key={c.key}
                  className={clsx(
                    'border-b-2 border-r border-[var(--border-color)] bg-orange-50 whitespace-nowrap last:border-r-0',
                    'font-black uppercase tracking-wide text-accent text-center',
                    TH_DENSITY,
                  )}
                  style={{ width: c.width, minWidth: c.minWidth }}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={visibleColumns.length} className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && displayRows.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length} className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
                  {empty ?? 'No records found'}
                </td>
              </tr>
            )}
            {!loading &&
              pagedRows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className="border-b border-[var(--border-color)] last:border-b-0 odd:bg-[var(--bg-secondary)]/60 hover:bg-[var(--bg-secondary)]"
                >
                  {visibleColumns.map((c) => (
                    <td
                      key={c.key}
                      className={clsx(
                        'border-r border-[var(--border-color)] font-semibold text-[var(--text-primary)] whitespace-nowrap text-center last:border-r-0',
                        TD_DENSITY,
                      )}
                      style={{ minWidth: c.minWidth }}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {(footer || (paginated && totalRows > 0)) && (
        <div ref={footerRef} className="border-t border-[var(--border-color)] px-2 py-1.5 text-xs text-[var(--text-muted)] sm:px-4 sm:py-2">
          {footer}
          {paginated && totalRows > 0 && (
            <Pagination page={pageSafe} pageSize={pageSize} total={totalRows} setPage={setPage} setPageSize={setPageSize} />
          )}
        </div>
      )}
    </div>
  );
}
